/**
 * Platform Admin Authentication Store
 *
 * Manages authentication state for control plane administrators.
 * Separate from main app auth - uses platform JWT tokens.
 */
import { create } from 'zustand';

export interface PlatformAdmin {
  id: string;
  email: string;
  name: string;
  organizationId: string | null;
  isSuperAdmin: boolean;
  isPrimary: boolean;
  activeSessions?: number;
}

interface PlatformAuthState {
  admin: PlatformAdmin | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  setAdmin: (admin: PlatformAdmin | null) => void;
  setAccessToken: (token: string | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearAuth: () => void;
}

export const usePlatformAuthStore = create<PlatformAuthState>((set) => ({
  admin: null,
  accessToken: null,
  isAuthenticated: false,
  isLoading: true, // Start true to check auth on mount
  error: null,

  setAdmin: (admin) =>
    set({
      admin,
      isAuthenticated: !!admin,
      error: null,
    }),

  setAccessToken: (accessToken) =>
    set({
      accessToken,
      isAuthenticated: !!accessToken,
    }),

  setLoading: (isLoading) => set({ isLoading }),

  setError: (error) => set({ error, isLoading: false }),

  clearAuth: () =>
    set({
      admin: null,
      accessToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    }),
}));
